import type { ReactElement } from 'react';
import type { DashboardComponent, TotalsComponent } from 'common/types/dashboard/dashboardDefinition';
import { Box, Text } from 'components/ui';
import ChartCard from './ChartCard';
import { COMPONENT_TYPE_LABELS, describeComponentFields } from './dashboardComponent.helpers';
import DashboardComponentEditButton from './DashboardComponentEditButton';
import {
  tileBadgeSx,
  tileBodySx,
  tileCardSx,
  tileFieldChipSx,
  tileFieldRowSx,
  tileHeaderActionsSx,
  tileHeaderSx,
  tileHighlightCaptionSx,
  tileHighlightValueSx,
  tilePlaceholderSx,
  tileSubtitleSx,
  tileTagRowSx,
  tileTitleSx,
  tileTypeChipSx,
} from './DashboardGrid.styles';
import TotalsCard from './TotalsCard';
import type { ResolvedComponentData } from '../../dashboardData.types';
import { SUMMARY_DASHBOARD_TEST_IDS } from '../../SummaryDashboard.constants';

interface DashboardComponentCardProps {
  component: DashboardComponent;
  componentData?: ResolvedComponentData;
  currencyCode?: string;
  isDataLoading?: boolean;
  isDataError?: boolean;
  // Opens the Edit Summary dialog on this component's tab. Absent when the definition is not
  // editable, which is what hides the button rather than a separate `canEdit` flag.
  onEdit?: () => void;
}

// A content card is complete without data — its body is the AI's own prose. Table and list tiles
// are still showing their configuration while they wait for a data contract of their own; `totals`
// and the three chart types have one and render through TotalsCard and ChartCard instead.
const PLACEHOLDER_LABEL = 'Data connection coming soon';

const DashboardComponentCard = ({
  component,
  componentData,
  currencyCode,
  isDataLoading,
  isDataError,
  onEdit,
}: DashboardComponentCardProps): ReactElement => {
  const { display = {}, type } = component;

  // Totals owns its whole tile: no type chip, no field chips, and no display.highlight, which is an
  // AI-authored string that would sit next to (and could contradict) the computed figure.
  if (type === 'totals') {
    return (
      <TotalsCard
        component={component as TotalsComponent}
        currencyCode={currencyCode}
        data={componentData}
        isError={isDataError}
        isLoading={isDataLoading}
        onEdit={onEdit}
      />
    );
  }

  if (type === 'bar_chart' || type === 'line_chart' || type === 'pie_chart') {
    return (
      <ChartCard
        component={component}
        currencyCode={currencyCode}
        data={componentData}
        isError={isDataError}
        isLoading={isDataLoading}
        onEdit={onEdit}
      />
    );
  }

  const fieldLabels = describeComponentFields(component);
  const isContentCard = type === 'content_card';

  return (
    <Box
      data-testid={SUMMARY_DASHBOARD_TEST_IDS.componentCard}
      data-component-type={type}
      id={component.id}
      sx={tileCardSx}>
      <Box sx={tileHeaderSx}>
        <Box sx={{ minWidth: 0 }}>
          {Boolean(display.title) && (
            <Text component="p" data-testid={SUMMARY_DASHBOARD_TEST_IDS.componentCardTitle} sx={tileTitleSx}>
              {display.title}
            </Text>
          )}
          {Boolean(display.subtitle) && (
            <Text component="p" sx={tileSubtitleSx}>
              {display.subtitle}
            </Text>
          )}
        </Box>
        <Box sx={tileHeaderActionsSx}>
          <Box component="span" sx={tileTypeChipSx}>
            {COMPONENT_TYPE_LABELS[type]}
          </Box>
          {onEdit && (
            <DashboardComponentEditButton
              label={display.title?.trim() || COMPONENT_TYPE_LABELS[type]}
              onClick={onEdit}
            />
          )}
        </Box>
      </Box>

      {Boolean(display.badge?.text) && (
        <Box component="span" sx={tileBadgeSx}>
          {display.badge?.text}
        </Box>
      )}

      {Boolean(display.highlight?.value) && (
        <Box>
          <Text component="p" sx={tileHighlightValueSx}>
            {display.highlight?.value}
          </Text>
          {Boolean(display.highlight?.caption) && (
            <Text component="p" sx={tileHighlightCaptionSx}>
              {display.highlight?.caption}
            </Text>
          )}
        </Box>
      )}

      {Boolean(display.description) && (
        <Text component="p" sx={tileBodySx}>
          {display.description}
        </Text>
      )}

      {Boolean(display.tags?.length) && (
        <Box sx={tileTagRowSx}>
          {display.tags?.map(tag => (
            <Box component="span" key={tag} sx={tileFieldChipSx}>
              {tag}
            </Box>
          ))}
        </Box>
      )}

      {fieldLabels.length > 0 && (
        <Box sx={tileFieldRowSx}>
          {fieldLabels.map(label => (
            <Box component="span" key={label} sx={tileFieldChipSx}>
              {label}
            </Box>
          ))}
        </Box>
      )}

      {!isContentCard && (
        <Box data-testid={SUMMARY_DASHBOARD_TEST_IDS.componentCardPlaceholder} sx={tilePlaceholderSx}>
          {PLACEHOLDER_LABEL}
        </Box>
      )}
    </Box>
  );
};

export default DashboardComponentCard;
